import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "../ui/sheet";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import {
  HelpCircle, ArrowRight, Lightbulb, BookOpen, ChevronRight,
  Keyboard, GraduationCap, X, PlayCircle,
} from "lucide-react";
import { useProductTour } from "../../hooks/useProductTour";

const CONTEXT_HELP = {
  "/": {
    title: "Dashboard",
    tourId: "dashboard",
    tips: [
      "View key metrics at a glance — active jobs, pending POs, open sales orders",
      "Use Quick Actions to jump directly to creating new documents",
      "Alerts section shows critical notifications like low stock or overdue items",
    ],
    shortcuts: [
      { keys: ["Ctrl", "K"], desc: "Open global search" },
    ],
    relatedGuide: "settings",
  },
  "/purchase": {
    title: "Purchase Orders",
    tourId: "purchase",
    tips: [
      "Click '+ New PO' to create a purchase order for suppliers",
      "Click any row to see PO details, GRNs, and print options",
      "Use 'Download PDF' to generate supplier-ready purchase orders",
      "PO numbers follow the format PO-YYYY-NNNN (auto-generated)",
    ],
    shortcuts: [
      { keys: ["Ctrl", "K"], desc: "Search POs quickly" },
    ],
    workflow: "Create PO → Approve → Receive GRN → QC Check",
    relatedGuide: "purchase",
  },
  "/production": {
    title: "Production",
    tourId: "production",
    tips: [
      "Create Work Orders to assign production work to machines",
      "Log production entries per shift to track output",
      "Create bundles to group finished pipes for dispatch",
      "Track yield % and scrap automatically per Work Order",
    ],
    workflow: "Work Order → Start → Log Entries → Create Bundles → Complete",
    relatedGuide: "production",
  },
  "/inventory": {
    title: "Inventory",
    tourId: "inventory",
    tips: [
      "View stock categorized: RM, WIP, FG Prime, B-Grade, Scrap",
      "Click category cards to filter the inventory view",
      "Use '+ Stock Adjustment' for manual corrections",
      "Search by description, grade, or heat number",
    ],
    relatedGuide: "inventory",
  },
  "/quality": {
    title: "Quality Control",
    tourId: "quality",
    tips: [
      "Create QC inspections for received or produced material",
      "Mark items as Pass, Fail, or Hold",
      "Failed material can be moved to B-Grade or Scrap",
      "QC results affect inventory categorization automatically",
    ],
    relatedGuide: "production",
  },
  "/sales": {
    title: "Sales Orders",
    tourId: "sales",
    tips: [
      "Create Sales Orders with customer details, pipe specs, and delivery dates",
      "Set tolerance % for delivery flexibility",
      "Track dispatch status from the order detail page",
      "Use Smart Buttons to see related GRNs, invoices, and dispatches",
    ],
    workflow: "Quotation → SO → Production Plan → Work Order → Dispatch → Invoice",
    relatedGuide: "sales",
  },
  "/quotations": {
    title: "Quotations & Proforma",
    tips: [
      "Create quotations before confirming an order",
      "Convert accepted quotations directly to Sales Orders",
      "Generate Proforma Invoices for advance payment requests",
      "Download professional PDFs with your company letterhead",
    ],
    relatedGuide: "sales",
  },
  "/finance": {
    title: "Finance & Invoicing",
    tourId: "finance",
    tips: [
      "Generate GST-compliant Tax Invoices with auto-calculated tax",
      "CGST+SGST for intra-state, IGST for inter-state transactions",
      "Download landscape PDF invoices with HSN summary",
      "Record partial or full payments against invoices",
    ],
    relatedGuide: "finance",
  },
  "/planning": {
    title: "Production Planning",
    tips: [
      "Create production plans from pending Sales Orders",
      "System auto-generates production requirements",
      "Convert plans to Work Orders for execution",
    ],
    relatedGuide: "sales",
  },
  "/settings": {
    title: "System Settings",
    tourId: "settings",
    tips: [
      "Configure master data: Grades, Pipe Sizes, Machines, Shifts",
      "Set up number series for auto-generated document numbers",
      "Configure Terms & Conditions for different document types",
      "Manage production parameters like weight calculation factors",
    ],
    relatedGuide: "settings",
  },
  "/company": {
    title: "Company Profile",
    tips: [
      "Enter your company name, address, and GST number",
      "Add bank details (shown on invoices for payment)",
      "This information appears on all generated PDFs",
    ],
    relatedGuide: "settings",
  },
  "/users": {
    title: "User Management",
    tips: [
      "Add employees and assign roles for access control",
      "Each role defines which modules a user can view/edit",
      "Active/inactive toggle controls user login access",
    ],
    relatedGuide: "settings",
  },
  "/roles": {
    title: "Roles & Access",
    tips: [
      "Define roles with specific module permissions",
      "Permissions: View, Create, Edit, Delete, Approve",
      "Admin role has full system access by default",
    ],
    relatedGuide: "settings",
  },
  "/reports": {
    title: "Reports",
    tips: [
      "View production, sales, inventory, and financial reports",
      "Export data to Excel or PDF format",
      "Filter reports by date range and financial year",
    ],
    relatedGuide: "settings",
  },
  "/masters": {
    title: "Master Data",
    tips: [
      "Manage Grades (API 5L, A106, etc.), Pipe Sizes, Parties",
      "Add machines with capacity and location details",
      "Configure shifts and warehouses",
    ],
    relatedGuide: "settings",
  },
  "/crm": {
    title: "CRM & Leads",
    tourId: "crm",
    tips: [
      "View your sales pipeline in Kanban — drag leads between stages",
      "Click '+ New Lead' to add a prospect with company, contact, and estimated value",
      "Open any lead to add activities, notes, and track follow-ups",
      "Convert won leads directly to Sales Orders",
    ],
    shortcuts: [{ keys: ["Ctrl", "K"], desc: "Search leads" }],
    workflow: "New → Contacted → Qualified → Proposal → Negotiation → Won/Lost",
    relatedGuide: "crm",
  },
  "/tasks": {
    title: "Task Management",
    tourId: "tasks",
    tips: [
      "Switch between List, Kanban, Calendar, Timeline, and Pivot views",
      "In Kanban view, drag task cards between columns to change status instantly",
      "Use filter buttons to view tasks by status (Open, In Progress, Review, Done, Blocked)",
      "Click any task to view details, add comments, or reassign",
    ],
    shortcuts: [{ keys: ["Ctrl", "K"], desc: "Search tasks" }],
    workflow: "Open → In Progress → Under Review → Completed",
    relatedGuide: "tasks",
  },
  "/approvals": {
    title: "Approvals",
    tips: [
      "View all pending approval requests assigned to you",
      "Click any request to review details and approve or reject with comments",
      "Delegate your approvals when going on leave",
    ],
    relatedGuide: "approvals",
  },
  "/hr": {
    title: "HR & Employees",
    tips: [
      "View employee directory with department, designation, and status",
      "Click any employee for full details — personal, salary, leave balance",
      "Generate monthly salary slips with auto-calculated deductions",
    ],
    relatedGuide: "hr",
  },
  "/leaves": {
    title: "Leave Management",
    tips: [
      "Apply for leave by selecting type, dates, and reason",
      "View leave balances by type (casual, sick, earned)",
      "Managers see team leave applications for approval",
    ],
    relatedGuide: "hr",
  },
  "/documents": {
    title: "Documents",
    tips: [
      "Upload and organize documents — PDFs, images, spreadsheets",
      "Tag documents and link to specific records (SO, PO, JO)",
      "Search by filename, tag, or linked document",
    ],
    relatedGuide: "documents",
  },
  "/maintenance": {
    title: "Maintenance",
    tips: [
      "Create maintenance orders — preventive, corrective, or breakdown",
      "Track machine downtime and maintenance history",
      "Schedule recurring maintenance tasks",
    ],
    relatedGuide: "maintenance",
  },
  "/expenses": {
    title: "Expenses",
    tips: [
      "Submit expense claims with receipt attachments",
      "Track expense status — submitted, approved, reimbursed",
      "View expense reports by category and date range",
    ],
    relatedGuide: "expenses",
  },
  "/oee": {
    title: "OEE Dashboard",
    tips: [
      "Monitor Overall Equipment Effectiveness per machine",
      "OEE = Availability x Performance x Quality",
      "Identify bottlenecks and underperforming machines",
    ],
    relatedGuide: "maintenance",
  },
  "/assets": {
    title: "Asset Management",
    tips: [
      "Track all company assets with purchase details and depreciation",
      "Assign assets to departments and locations",
      "View maintenance history per asset",
    ],
    relatedGuide: "maintenance",
  },
  "/audit-trail": {
    title: "Audit Trail",
    tips: [
      "View complete change history across all modules",
      "Filter by user, module, action type, or date range",
      "See exactly who changed what and when",
    ],
    relatedGuide: "security",
  },
  "/ai": {
    title: "AI Features",
    tips: [
      "Command Center: Ask natural language questions about your data",
      "Anomaly Center: Auto-detects unusual patterns in your business",
      "AI Dispatch: Optimized delivery scheduling",
    ],
    relatedGuide: "ai",
  },
  "/workflow": {
    title: "Workflow Engine",
    tips: [
      "Visual workflow dashboard showing all active automations",
      "Process chains trigger actions automatically (e.g., SO confirmed → create JO)",
      "Monitor workflow execution logs for debugging",
    ],
    relatedGuide: "customization",
  },
  "/contractors": {
    title: "Contractor Management",
    tips: [
      "Track contractor details, rates, and work assignments",
      "Record contractor attendance and calculate payments",
    ],
    relatedGuide: "hr",
  },
  "/visitors": {
    title: "Visitor Management",
    tips: [
      "Pre-register expected visitors with host details",
      "Track check-in/out times at security gate",
      "Print visitor badges automatically",
    ],
    relatedGuide: "security",
  },
  "/projects": {
    title: "Projects",
    tips: [
      "Create and track projects with milestones and deadlines",
      "Assign team members and monitor progress",
    ],
    relatedGuide: "tasks",
  },
  "/contracts-tracker": {
    title: "Contracts",
    tips: [
      "Track all active contracts with customers and vendors",
      "Set renewal reminders and expiry alerts",
      "Link contracts to related orders and invoices",
    ],
    relatedGuide: "sales",
  },
};

function getContextForPath(pathname) {
  // Exact match first
  if (CONTEXT_HELP[pathname]) return CONTEXT_HELP[pathname];
  // Parent path match
  const parts = pathname.split("/").filter(Boolean);
  if (parts.length > 1) {
    const parent = "/" + parts[0];
    if (CONTEXT_HELP[parent]) return CONTEXT_HELP[parent];
  }
  return null;
}

export default function ContextHelp() {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { startTour } = useProductTour();

  const context = getContextForPath(location.pathname);
  if (!context) return null;

  const handleStartTour = () => {
    setOpen(false);
    if (context.tourId) {
      setTimeout(() => startTour(context.tourId), 300);
    }
  };

  return (
    <>
      {/* Floating help button */}
      <button
        onClick={() => setOpen(true)}
        className="fixed bottom-20 right-6 z-50 w-12 h-12 rounded-full bg-primary text-primary-foreground shadow-lg hover:shadow-xl hover:scale-105 transition-all flex items-center justify-center group"
        data-testid="context-help-btn"
      >
        <HelpCircle className="w-5 h-5 group-hover:hidden" />
        <span className="hidden group-hover:block text-xs font-bold">Help</span>
      </button>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="right" className="w-[380px] sm:max-w-[380px] p-0 flex flex-col">
          <SheetHeader className="p-5 pb-3 border-b">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                <Lightbulb className="w-4 h-4 text-primary" />
              </div>
              <div>
                <SheetTitle className="text-base">{context.title}</SheetTitle>
                <SheetDescription className="text-xs">Quick help for this page</SheetDescription>
              </div>
            </div>
          </SheetHeader>

          <div className="flex-1 overflow-y-auto p-5 space-y-5">
            {/* Interactive Tour CTA */}
            {context.tourId && (
              <button
                onClick={handleStartTour}
                className="w-full p-4 rounded-xl bg-gradient-to-r from-primary/10 to-primary/5 border border-primary/15 hover:from-primary/15 hover:to-primary/10 transition-all text-left group"
                data-testid="context-help-start-tour-btn"
              >
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-lg bg-primary/15 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                    <PlayCircle className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-primary">Start Interactive Tour</p>
                    <p className="text-[11px] text-muted-foreground mt-0.5">Step-by-step guided walkthrough of this page</p>
                  </div>
                </div>
              </button>
            )}

            {/* Tips */}
            <div>
              <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">Tips</h4>
              <div className="space-y-2.5">
                {context.tips.map((tip, i) => (
                  <div key={i} className="flex items-start gap-2.5">
                    <div className="w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-[10px] font-bold text-primary">{i + 1}</span>
                    </div>
                    <p className="text-sm text-muted-foreground leading-relaxed">{tip}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Workflow */}
            {context.workflow && (
              <div className="p-3.5 rounded-md bg-primary/5 border border-primary/10">
                <h4 className="text-xs font-semibold text-primary mb-1.5 flex items-center gap-1.5">
                  <ArrowRight className="w-3 h-3" />
                  Workflow
                </h4>
                <p className="text-sm text-muted-foreground font-mono">{context.workflow}</p>
              </div>
            )}

            {/* Keyboard Shortcuts */}
            {context.shortcuts && context.shortcuts.length > 0 && (
              <div>
                <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3 flex items-center gap-1.5">
                  <Keyboard className="w-3 h-3" />
                  Shortcuts
                </h4>
                <div className="space-y-2">
                  {context.shortcuts.map((s, i) => (
                    <div key={i} className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">{s.desc}</span>
                      <div className="flex items-center gap-1">
                        {s.keys.map((k) => (
                          <kbd key={k} className="px-1.5 py-0.5 rounded-md bg-muted border text-[10px] font-mono font-medium">{k}</kbd>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="p-4 border-t space-y-2">
            <Button
              variant="outline"
              size="sm"
              className="w-full rounded-md gap-2 justify-between"
              onClick={() => { setOpen(false); navigate("/help"); }}
              data-testid="context-help-help-center-btn"
            >
              <span className="flex items-center gap-2">
                <BookOpen className="w-4 h-4" />
                Open Help Center
              </span>
              <ChevronRight className="w-3.5 h-3.5" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="w-full rounded-md gap-2 justify-between"
              onClick={() => { setOpen(false); navigate("/training"); }}
              data-testid="context-help-full-guide-btn"
            >
              <span className="flex items-center gap-2">
                <GraduationCap className="w-4 h-4" />
                Open Full Training Guide
              </span>
              <ChevronRight className="w-3.5 h-3.5" />
            </Button>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
